// A press held down, for a control that has a second thing to offer.
//
// The gutter already uses this shape to lift a paragraph (useGutter), and the
// timing matches it: 500ms, abandoned if the finger travels. Desktop reaches
// the same thing with a right-click, which is the gesture people already use
// to ask "what else can this do?".
//
// The subtlety is the click that follows. A long press on a button still ends
// in a click, which would fire the button's ordinary action on top of opening
// the menu — so the press is prevented at pointerdown, and a flag catches any
// click that gets through anyway.

import { type PointerEvent as ReactPointerEvent, useCallback, useRef } from "react";

const LONG_PRESS_MS = 500;
const MOVE_SLOP_PX = 8;

export interface LongPress {
  /** Spread onto the button, alongside its own onClick. */
  handlers: {
    onPointerDown: (e: ReactPointerEvent) => void;
    onPointerMove: (e: ReactPointerEvent) => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
    onContextMenu: (e: React.MouseEvent) => void;
  };
  /** True when the click now arriving is the tail of a long press, and the
   *  button's ordinary action should sit this one out. */
  swallowed: () => boolean;
}

export function useLongPress(onHold: () => void): LongPress {
  const held = useRef<{ x: number; y: number; timer: number } | null>(null);
  const swallow = useRef(false);
  const hold = useRef(onHold);
  hold.current = onHold;

  const cancel = useCallback(() => {
    if (held.current) window.clearTimeout(held.current.timer);
    held.current = null;
  }, []);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      // A mouse's right button is the other gesture, handled by onContextMenu.
      if (e.button !== 0) return;
      cancel();
      const timer = window.setTimeout(() => {
        held.current = null;
        swallow.current = true;
        hold.current();
      }, LONG_PRESS_MS);
      held.current = { x: e.clientX, y: e.clientY, timer };
    },
    [cancel],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent) => {
      const at = held.current;
      if (!at) return;
      if (Math.abs(e.clientX - at.x) > MOVE_SLOP_PX || Math.abs(e.clientY - at.y) > MOVE_SLOP_PX) {
        cancel();
      }
    },
    [cancel],
  );

  return {
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: cancel,
      onPointerCancel: cancel,
      onContextMenu: (e: React.MouseEvent) => {
        e.preventDefault();
        cancel();
        hold.current();
      },
    },
    swallowed: () => {
      if (!swallow.current) return false;
      swallow.current = false;
      return true;
    },
  };
}
